
import { useState, useCallback, useEffect } from 'react';
import { mockDramas } from '../mocks/dramas';
import { aiProjects } from '../mocks/aiProjects';
import { createDramaUrl, createAIProjectUrl } from './useNavigation';

export type WatchStatus = 'watching' | 'completed' | 'plan-to-watch';

export interface WatchlistItem {
  id: string;
  type: 'drama' | 'ai-project';
  title: string;
  poster: string;
  rating: number;
  genre: string;
  year: number | string;
  episodes?: number;
  duration: string;
  status: WatchStatus;
  addedDate: string;
  progress?: number;
  totalEpisodes?: number;
  url: string;
}

const WATCHLIST_STORAGE_KEY = 'dramahub_watchlist';

const getStoredWatchlist = (): WatchlistItem[] => {
  try {
    const stored = localStorage.getItem(WATCHLIST_STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch {
    // ignore
  }
  return getDefaultWatchlist();
};

const getDefaultWatchlist = (): WatchlistItem[] => [
  {
    id: 'drama-001',
    type: 'drama',
    title: mockDramas[0].title,
    poster: mockDramas[0].poster,
    rating: mockDramas[0].rating,
    genre: mockDramas[0].genre.join(', '),
    year: mockDramas[0].year,
    episodes: mockDramas[0].episodes,
    duration: mockDramas[0].duration,
    status: 'watching',
    addedDate: '2025-01-20',
    progress: 7,
    totalEpisodes: mockDramas[0].episodes,
    url: createDramaUrl(mockDramas[0].genreSlug, mockDramas[0].slug),
  },
  {
    id: 'drama-003',
    type: 'drama',
    title: mockDramas[2].title,
    poster: mockDramas[2].poster,
    rating: mockDramas[2].rating,
    genre: mockDramas[2].genre.join(', '),
    year: mockDramas[2].year,
    episodes: mockDramas[2].episodes,
    duration: mockDramas[2].duration,
    status: 'watching',
    addedDate: '2025-01-18',
    progress: 3,
    totalEpisodes: mockDramas[2].episodes,
    url: createDramaUrl(mockDramas[2].genreSlug, mockDramas[2].slug),
  },
  {
    id: 'drama-002',
    type: 'drama',
    title: mockDramas[1].title,
    poster: mockDramas[1].poster,
    rating: mockDramas[1].rating,
    genre: mockDramas[1].genre.join(', '),
    year: mockDramas[1].year,
    episodes: mockDramas[1].episodes,
    duration: mockDramas[1].duration,
    status: 'completed',
    addedDate: '2025-01-10',
    progress: mockDramas[1].episodes,
    totalEpisodes: mockDramas[1].episodes,
    url: createDramaUrl(mockDramas[1].genreSlug, mockDramas[1].slug),
  },
  {
    id: 'ai-1',
    type: 'ai-project',
    title: aiProjects[0].title,
    poster: aiProjects[0].thumbnail,
    rating: aiProjects[0].averageRating || 4.8,
    genre: aiProjects[0].category,
    year: aiProjects[0].datePublished.split('-')[0],
    duration: aiProjects[0].duration,
    status: 'completed',
    addedDate: '2025-01-15',
    url: createAIProjectUrl(aiProjects[0].slug),
  },
  {
    id: 'ai-4',
    type: 'ai-project',
    title: aiProjects[3].title,
    poster: aiProjects[3].thumbnail,
    rating: aiProjects[3].averageRating || 4.7,
    genre: aiProjects[3].category,
    year: aiProjects[3].datePublished.split('-')[0],
    duration: aiProjects[3].duration,
    status: 'watching',
    addedDate: '2025-01-22',
    url: createAIProjectUrl(aiProjects[3].slug),
  },
  {
    id: 'drama-007',
    type: 'drama',
    title: mockDramas[6].title,
    poster: mockDramas[6].poster,
    rating: mockDramas[6].rating,
    genre: mockDramas[6].genre.join(', '),
    year: mockDramas[6].year,
    episodes: mockDramas[6].episodes,
    duration: mockDramas[6].duration,
    status: 'plan-to-watch',
    addedDate: '2025-01-25',
    progress: 0,
    totalEpisodes: mockDramas[6].episodes,
    url: createDramaUrl(mockDramas[6].genreSlug, mockDramas[6].slug),
  },
  {
    id: 'ai-5',
    type: 'ai-project',
    title: aiProjects[4].title,
    poster: aiProjects[4].thumbnail,
    rating: aiProjects[4].averageRating || 4.9,
    genre: aiProjects[4].category,
    year: aiProjects[4].datePublished.split('-')[0],
    duration: aiProjects[4].duration,
    status: 'plan-to-watch',
    addedDate: '2025-01-24',
    url: createAIProjectUrl(aiProjects[4].slug),
  },
  {
    id: 'drama-005',
    type: 'drama',
    title: mockDramas[4].title,
    poster: mockDramas[4].poster,
    rating: mockDramas[4].rating,
    genre: mockDramas[4].genre.join(', '),
    year: mockDramas[4].year,
    episodes: mockDramas[4].episodes,
    duration: mockDramas[4].duration,
    status: 'plan-to-watch',
    addedDate: '2025-01-23',
    progress: 0,
    totalEpisodes: mockDramas[4].episodes,
    url: createDramaUrl(mockDramas[4].genreSlug, mockDramas[4].slug),
  },
];

// Singleton pattern for shared state across components
let globalWatchlist: WatchlistItem[] = getStoredWatchlist();
let listeners: Array<() => void> = [];

const notifyListeners = () => {
  listeners.forEach((fn) => fn());
};

const persistWatchlist = (items: WatchlistItem[]) => {
  globalWatchlist = items;
  localStorage.setItem(WATCHLIST_STORAGE_KEY, JSON.stringify(items));
  notifyListeners();
};

export const useWatchlist = () => {
  const [items, setItems] = useState<WatchlistItem[]>(globalWatchlist);

  useEffect(() => {
    const listener = () => {
      setItems([...globalWatchlist]);
    };
    listeners.push(listener);
    // Sync on mount
    setItems([...globalWatchlist]);
    return () => {
      listeners = listeners.filter((fn) => fn !== listener);
    };
  }, []);

  const isInWatchlist = useCallback((id: string): boolean => {
    return globalWatchlist.some((item) => item.id === id);
  }, [items]);

  const getWatchlistItem = useCallback((id: string): WatchlistItem | undefined => {
    return globalWatchlist.find((item) => item.id === id);
  }, [items]);

  const addToWatchlist = useCallback((item: Omit<WatchlistItem, 'addedDate' | 'status'> & { status?: WatchStatus }) => {
    if (globalWatchlist.some((i) => i.id === item.id)) return;
    const newItem: WatchlistItem = {
      ...item,
      status: item.status || 'plan-to-watch',
      addedDate: new Date().toISOString().split('T')[0],
    };
    persistWatchlist([newItem, ...globalWatchlist]);
  }, []);

  const removeFromWatchlist = useCallback((id: string) => {
    persistWatchlist(globalWatchlist.filter((item) => item.id !== id));
  }, []);

  const updateStatus = useCallback((id: string, status: WatchStatus) => {
    persistWatchlist(
      globalWatchlist.map((item) =>
        item.id === id ? { ...item, status } : item
      )
    );
  }, []);

  const toggleWatchlist = useCallback((item: Omit<WatchlistItem, 'addedDate' | 'status'> & { status?: WatchStatus }): boolean => {
    if (globalWatchlist.some((i) => i.id === item.id)) {
      removeFromWatchlist(item.id);
      return false;
    }
    addToWatchlist(item);
    return true;
  }, [addToWatchlist, removeFromWatchlist]);

  return {
    items,
    isInWatchlist,
    getWatchlistItem,
    addToWatchlist,
    removeFromWatchlist,
    updateStatus,
    toggleWatchlist,
  };
};
